

export const dynamic = 'force-dynamic';

import { useState } from "react";
import VendorReportsLayout, { useVendorPeriod, type Period } from "../VendorReportsLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import {
  Card, ExportButtons, Kpi, KpiGrid, ReportBody, TableScroll, money, th, td, useCanSeeMoney, useReport, type ExportSpec,
} from "../../ReportKit";

interface Assignment {
  key: string;
  type: "vendor" | "service";
  vendor_id: number | null;
  vendor_name: string;
  vendor_type: string | null;
  event_id: number | null;
  event_name: string;
  client_name: string | null;
  event_date: string | null;
  event_status: string | null;
  description: string | null;
  quantity: number;
  unit: string | null;
  cost?: number;
  paid?: number | null;
}

interface AssignmentsReport {
  can_view_money: boolean;
  summary: { total_assignments: number; events: number; vendors: number; total_cost?: number; total_paid?: number };
  assignments: { data: Assignment[]; current_page: number; last_page: number; per_page: number; total: number };
}

const statusColors: Record<string, string> = {
  confirmed: "bg-emerald-100 text-emerald-700",
  draft: "bg-stone-100 text-stone-600",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-violet-100 text-violet-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const statusText = (s: string | null | undefined) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "");

/** The server's largest page; the export walks pages up to EXPORT_MAX_ROWS rows. */
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 1000;

/** Excel / Print: every piece of work in the period (not just the page on screen), money only for roles that see it. */
async function buildExport(period: Period, statusFilter: string, canSeeMoney: boolean): Promise<ExportSpec> {
  const fetchPage = async (page: number): Promise<AssignmentsReport> => (await reportsApi.vendorAssignments({
    from_date: period.from, to_date: period.to, status: statusFilter, page, per_page: EXPORT_PAGE_SIZE,
  })).data;
  const first = await fetchPage(1);
  const rows: Assignment[] = [...first.assignments.data];
  const pages = Math.min(first.assignments.last_page, EXPORT_MAX_ROWS / EXPORT_PAGE_SIZE);
  for (let page = 2; page <= pages; page++) rows.push(...(await fetchPage(page)).assignments.data);

  const s = first.summary;
  const total = first.assignments.total;
  const showMoney = canSeeMoney && first.can_view_money;
  return {
    title: "Vendor Assignments",
    fileName: "vendor-assignments",
    period,
    summary: [
      { label: "Pieces of work", value: s.total_assignments, type: "number" },
      { label: "Vendors", value: s.vendors, type: "number" },
      { label: "Events", value: s.events, type: "number" },
      ...(showMoney
        ? [
          { label: "Cost of work", value: s.total_cost, type: "money" as const },
          { label: "Paid to vendors", value: s.total_paid, type: "money" as const },
        ]
        : []),
    ],
    tables: [{
      title: "Work given to vendors",
      columns: [
        { label: "Vendor" }, { label: "Category" }, { label: "Work" }, { label: "Kind" },
        { label: "Event" }, { label: "Client" }, { label: "Date", type: "date" }, { label: "Event status" },
        { label: "Qty" }, { label: "Unit" },
        ...(showMoney ? [{ label: "Cost", type: "money" as const }, { label: "Paid", type: "money" as const }] : []),
      ],
      rows: rows.map((a) => [
        a.vendor_name, a.vendor_type, a.description, a.type === "service" ? "Service" : "Vendor line",
        a.event_name, a.client_name, a.event_date, statusText(a.event_status),
        a.quantity, a.unit,
        ...(showMoney ? [a.cost, a.paid] : []),
      ]),
    }],
    notes: [
      "Vendor lines and services given to a vendor, latest event first.",
      ...(statusFilter ? [`Only events that are: ${statusText(statusFilter)}.`] : []),
      ...(showMoney ? ["Payments for service work are recorded on the vendor line, so Paid is blank for services."] : []),
      ...(rows.length < total ? [`Showing the first ${rows.length} of ${total} pieces of work.`] : []),
    ],
  };
}

export default function VendorAssignmentsPage() {
  const [period, setPeriod] = useVendorPeriod();
  const canSeeMoney = useCanSeeMoney();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const state = useReport<AssignmentsReport>(
    async () => (await reportsApi.vendorAssignments({
      from_date: period.from, to_date: period.to, status: statusFilter, page, per_page: 20,
    })).data,
    [period.from, period.to, statusFilter, page],
  );

  return (
    <VendorReportsLayout
      period={period}
      onPeriodChange={(p) => { setPeriod(p); setPage(1); }}
      actions={
        <ExportButtons
          build={() => buildExport(period, statusFilter, canSeeMoney)}
          disabled={state.loading || !state.data?.assignments.total}
        />
      }
    >
      <div>
        <select
          value={statusFilter}
          aria-label="Event status"
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-10 px-3 text-sm rounded-lg border border-stone-200 focus:border-amber-400 outline-none bg-white"
        >
          <option value="">Any event status</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <ReportBody state={state}>
        {(d) => {
          const showMoney = canSeeMoney && d.can_view_money;
          const s = d.summary;
          const { data: rows, current_page, last_page } = d.assignments;
          return (
            <>
              <KpiGrid>
                <Kpi label="Pieces of work" value={s.total_assignments} sub={`${s.vendors} vendor${s.vendors === 1 ? "" : "s"}`} />
                <Kpi label="Events" value={s.events} tone="emerald" />
                {showMoney && <Kpi label="Cost of work" value={money(s.total_cost)} tone="amber" sub="what it costs us" />}
                {showMoney && <Kpi label="Paid to vendors" value={money(s.total_paid)} tone="teal" sub="towards this work" />}
              </KpiGrid>

              <Card title="Work given to vendors" subtitle="Vendor lines and services given to a vendor, latest event first">
                {rows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-stone-400">No vendor work on events in this period.</p>
                ) : (
                  <>
                    <TableScroll minWidth={showMoney ? 860 : 640}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-stone-100">
                            <th className={`${th} text-left`}>Vendor</th>
                            <th className={`${th} text-left`}>Work</th>
                            <th className={`${th} text-left`}>Event</th>
                            <th className={`${th} text-left`}>Date</th>
                            <th className={`${th} text-center`}>Event status</th>
                            <th className={`${th} text-right`}>Qty</th>
                            {showMoney && <th className={`${th} text-right`}>Cost</th>}
                            {showMoney && <th className={`${th} text-right`}>Paid</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-50">
                          {rows.map((a) => (
                            <tr key={a.key} className="hover:bg-stone-50/50">
                              <td className={td}>
                                <div className="font-semibold text-stone-800">{a.vendor_name}</div>
                                {a.vendor_type && <div className="text-xs text-stone-400">{a.vendor_type}</div>}
                              </td>
                              <td className={`${td} text-stone-700`}>
                                {a.description || "—"}
                                <div className="text-[11px] text-stone-400">{a.type === "service" ? "Service" : "Vendor line"}</div>
                              </td>
                              <td className={td}>
                                <div className="font-medium text-stone-700">{a.event_name}</div>
                                {a.client_name && <div className="text-xs text-stone-400">{a.client_name}</div>}
                              </td>
                              <td className={`${td} text-xs text-stone-500`}>{a.event_date ? formatDate(a.event_date) : "—"}</td>
                              <td className={`${td} text-center`}>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[a.event_status ?? ""] ?? "bg-stone-100 text-stone-600"}`}>
                                  {a.event_status?.replace(/_/g, " ") ?? "—"}
                                </span>
                              </td>
                              <td className={`${td} text-right text-stone-600`}>{a.quantity} {a.unit || ""}</td>
                              {showMoney && <td className={`${td} text-right tabular-nums text-stone-800`}>{money(a.cost)}</td>}
                              {showMoney && <td className={`${td} text-right tabular-nums text-emerald-700`}>{a.paid === null || a.paid === undefined ? <span className="text-stone-400" title="Payments for service work are recorded on the vendor line">—</span> : money(a.paid)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>

                    {last_page > 1 && (
                      <div className="flex items-center justify-between pt-3 mt-3 border-t border-stone-100">
                        <button onClick={() => setPage(current_page - 1)} disabled={current_page <= 1}
                          className="h-10 px-3 text-sm rounded-lg border border-stone-200 disabled:opacity-40 hover:border-stone-300">
                          ← Previous
                        </button>
                        <span className="text-xs text-stone-500">Page {current_page} of {last_page}</span>
                        <button onClick={() => setPage(current_page + 1)} disabled={current_page >= last_page}
                          className="h-10 px-3 text-sm rounded-lg border border-stone-200 disabled:opacity-40 hover:border-stone-300">
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </>
          );
        }}
      </ReportBody>
    </VendorReportsLayout>
  );
}
